import { sumBy } from 'lodash';
import { cloneDeep, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IDashboardHomeCardMetricKey } from '~/src/dash/api/dashboards/home';
import {
  calculateSmartFloorCPM,
  calculateSmartFloorEstimatedRevenue,
  normalizeSmartFloorMetric,
} from '~/src/dash/apiV2/smartFloors/common';
import {
  calculateBidRequestPercentages,
  getAdaptiveSmartFloorLiveData,
  ISmartFloorRealtimeData,
  ISmartFloorRealtimeDataPerBidFloorAndPeriod,
  ISmartFloorRealtimeDataPerPeriod,
  parseBidFloorKey,
} from '~/src/dash/apiV2/smartFloors/realtimeAdaptive';
import { useInterval } from '~/src/dash/utils/hooks';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { useSmartFloorsLiveAdaptiveStore } from '~/src/dash/views/bidders/Bidders/SmartFloors/contexts';
import { SmartFloorsLiveAdaptive } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveAdaptiveView';
import {
  IStackedChartData,
  nonBidFloorKeysForChart,
} from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveAdaptiveView/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const SmartFloorsLiveAdaptiveContainer = (): React.ReactNode => {
  const { session } = useBaseStore();
  const { liveConfig, selectedSmartFloor, selectedSite } = useSmartFloorsLiveAdaptiveStore();
  const { defaultState } = liveConfig;

  // all states related to chart
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [data, setData] = useState([] as ISmartFloorRealtimeData); // realtime data
  const [chartData, setChartData] = useState([] as IStackedChartData[]); // parsed realtime data for the chart visualization
  const [isChartAggregatedByTime, setIsChartAggregatedByTime] = useState(false); // 'Smooth' toogle
  const [isChartAggregatedByBidFloorType, setIsChartAggregatedByBidFloorType] = useState(true); // 'Investigation' toogle
  const [bidFloorKeys, setBidFloorKeys] = useState([] as string[]); // all bid floors in the chart
  const [selectedMetricKey, setSelectedMetricKey] = useState(defaultState.selectedMetricKey); // dropdown selected option

  // for fetching live data
  const [isDataRefreshActive, setIsDataRefreshActive] = useState(false);
  const [lastRefreshedTime, setLastRefreshedTime] = useState('');

  // error snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  // calculate metrics for a bid floor, ex: cpm, estimated revenue, etc.
  const calculateBidFloorMetrics = (
    bidFloorData: ISmartFloorRealtimeDataPerBidFloorAndPeriod,
    bidRequestPercentage: number = 0,
    totalBidRequests: number = 0,
  ): ISmartFloorRealtimeDataPerBidFloorAndPeriod => {
    return {
      ...bidFloorData,
      BidRequestPercentage: bidRequestPercentage,
      CPM: parseFloat(
        calculateSmartFloorCPM(bidFloorData.CPM, bidFloorData.WinningBids).toFixed(2),
      ),
      EstimatedRevenue: parseFloat(
        calculateSmartFloorEstimatedRevenue(
          normalizeSmartFloorMetric(bidFloorData.CPM, bidFloorData.BidRequests, totalBidRequests),
        ).toFixed(2),
      ),
      TotalBidRequests: totalBidRequests,
    };
  };

  // aggregate live data by time and/or bid floor type
  const aggregateChartData = (
    liveData: ISmartFloorRealtimeData,
    shouldAggregateDataByTime: boolean,
    shouldAggregateDataByBidFloorType: boolean,
  ): ISmartFloorRealtimeData => {
    // cloneDeep so that we maintain a clean original version of liveData
    let aggregatedData = cloneDeep(liveData);

    // aggregate by time if needed
    if (shouldAggregateDataByTime) {
      const aggregatedDataByTime: ISmartFloorRealtimeData = [];

      // Aggregate every 3 10-min periods into one (30 minutes)
      for (let idx = 0; idx < aggregatedData.length; idx += 3) {
        const periodData: ISmartFloorRealtimeDataPerPeriod = aggregatedData[idx];
        if (aggregatedData[idx + 1]) {
          // Merge the next two periods into the current one
          Object.entries(aggregatedData[idx + 1]).forEach(([key, data]) => {
            if (periodData[key]) {
              periodData[key].BidRequests += data.BidRequests;
              periodData[key].BidResponses += data.BidResponses;
              periodData[key].CPM += data.CPM;
              periodData[key].WinningBids += data.WinningBids;
            } else {
              periodData[key] = { ...data };
            }
          });
        }
        if (aggregatedData[idx + 2]) {
          Object.entries(aggregatedData[idx + 2]).forEach(([key, data]) => {
            if (periodData[key]) {
              periodData[key].BidRequests += data.BidRequests;
              periodData[key].BidResponses += data.BidResponses;
              periodData[key].CPM += data.CPM;
              periodData[key].WinningBids += data.WinningBids;
            } else {
              periodData[key] = { ...data };
            }
          });
        }

        aggregatedDataByTime.push(periodData);
      }

      aggregatedData = aggregatedDataByTime;
    }

    // aggregate by bid floor type if needed
    if (shouldAggregateDataByBidFloorType) {
      aggregatedData.forEach((periodData, idx) => {
        const newPeriodData: ISmartFloorRealtimeDataPerPeriod = {};
        const smartBidFloorToBidRequests: Record<string, number> = {};
        const smartFloorData: ISmartFloorRealtimeDataPerBidFloorAndPeriod = {
          Date: periodData[Object.keys(periodData)[0]].Date,
          SmartFloorID: periodData[Object.keys(periodData)[0]].SmartFloorID,
          IsControlBidFloor: false,
          TotalBidRequests: 0,
          BidFloor: 0,
          BidRequests: 0,
          BidRequestPercentage: 0,
          EstimatedRevenue: 0,
          BidResponses: 0,
          CPM: 0,
          WinningBids: 0,
        };

        Object.entries(periodData).forEach(([key, data]) => {
          // if it's a control bid floor, add it to the new period data as is
          if (data.IsControlBidFloor) {
            newPeriodData.Control = data;
            return;
          }

          // collect smart bid floor -> bid requests to calculate weighted average later
          const { bidFloor } = parseBidFloorKey(key);
          smartBidFloorToBidRequests[bidFloor] = data.BidRequests;

          // increment smart floor data with current period data
          smartFloorData.BidRequests += data.BidRequests;
          smartFloorData.BidResponses += data.BidResponses;
          smartFloorData.CPM += data.CPM;
          smartFloorData.WinningBids += data.WinningBids;
        });

        // calculate weighted average smart bid floor
        const weightedAvgBidFloor = Object.keys(smartBidFloorToBidRequests).reduce(
          (acc, bidFloorStr: string) => {
            const bidRequests = smartBidFloorToBidRequests[bidFloorStr];
            if (bidRequests > 0) {
              return acc + Number(bidFloorStr) * (bidRequests / smartFloorData.BidRequests);
            }
            return acc;
          },
          0,
        );
        smartFloorData.BidFloor = Number(weightedAvgBidFloor.toFixed(2));

        // assign to the smart bidfloor data
        newPeriodData.Smart = smartFloorData;

        // replace aggregated data with new period data
        aggregatedData[idx] = newPeriodData;
      });
    }

    aggregatedData.forEach((periodData: ISmartFloorRealtimeDataPerPeriod) => {
      if (!periodData || isEmpty(periodData)) {
        return;
      }

      const totalBidRequests = sumBy(Object.values(periodData), (data) => data.BidRequests);

      // Calculate request percentages of each bid floor and ensure they sum to exactly 100
      const bidFloorToBidRequestPercentage = calculateBidRequestPercentages(
        periodData,
        totalBidRequests,
      );
      Object.keys(periodData).forEach((key) => {
        const bidFloorData = periodData[key];
        periodData[key] = calculateBidFloorMetrics(
          bidFloorData,
          bidFloorToBidRequestPercentage[key],
          totalBidRequests,
        );
      });
    });

    return aggregatedData;
  };

  // format live data for chart visualization
  const formatDataForChart = (
    liveData: ISmartFloorRealtimeData,
    selectedMetricKey: IDashboardHomeCardMetricKey,
    shouldAggregateDataByTime: boolean,
    shouldAggregateDataByBidFloorType: boolean,
  ): IStackedChartData[] => {
    // aggregate live data by time and bid floor type if needed
    const aggregatedData = aggregateChartData(
      liveData,
      shouldAggregateDataByTime,
      shouldAggregateDataByBidFloorType,
    );

    // transform aggregated data into chart data  for visualization
    return aggregatedData.map((periodData: ISmartFloorRealtimeDataPerPeriod): IStackedChartData => {
      const bidFloorAndTypeKeys = Object.keys(periodData);
      const date = periodData[bidFloorAndTypeKeys[0]].Date.format('YYYY-MM-DD HH:mm');

      const periodDataForChart: IStackedChartData = {
        date,
        controlBidFloorKey: '',
        weightedAvgControlBidFloor: 0,
        weightedAvgSmartBidFloor: 0,
      };

      bidFloorAndTypeKeys.forEach((key) => {
        const bidFloorData = periodData[key];
        if (bidFloorData) {
          // add control bid floor if it exists
          if (bidFloorData.IsControlBidFloor) {
            periodDataForChart.controlBidFloorKey = key;
          }

          // if aggregating by bid floor type (Investigation OFF), set the weighted average bid floors
          if (shouldAggregateDataByBidFloorType) {
            if (bidFloorData.IsControlBidFloor) {
              periodDataForChart.weightedAvgControlBidFloor = bidFloorData.BidFloor;
            } else {
              periodDataForChart.weightedAvgSmartBidFloor = bidFloorData.BidFloor;
            }
          }

          // get the value for the selected metric key
          const metricValue =
            bidFloorData[selectedMetricKey as keyof ISmartFloorRealtimeDataPerBidFloorAndPeriod];
          if (metricValue !== undefined && metricValue !== null) {
            if (typeof metricValue === 'number' || typeof metricValue === 'string') {
              periodDataForChart[key] = metricValue;
            }
          } else {
            periodDataForChart[key] = 0;
          }
        }
      });

      return periodDataForChart;
    });
  };

  // generate an ordered list of bid floors from live data
  const genBidFloorKeys = (
    chartData: IStackedChartData[],
    shouldAggregateDataByBidFloorType: boolean,
  ): string[] => {
    const uniqueBidFloorKeys = [
      ...new Set(
        chartData
          .map((datum: IStackedChartData) =>
            Object.keys(datum).filter((key) => !nonBidFloorKeysForChart.includes(key)),
          )
          .flat(),
      ),
    ];

    return uniqueBidFloorKeys.sort((a, b) => {
      // if aggregating by bid floor type (Investigation ON): a, b = Control, Smart
      if (shouldAggregateDataByBidFloorType) {
        return a.localeCompare(b);
      }

      // if not aggregating by bid floor type, sort by bid floor value and type
      const { bidFloor: bidFloorA, bidFloorType: bidFloorTypeA } = parseBidFloorKey(a);
      const { bidFloor: bidFloorB, bidFloorType: bidFloorTypeB } = parseBidFloorKey(b);
      return (
        Number(bidFloorA) * 100 +
        Number(bidFloorTypeA) -
        (Number(bidFloorB) * 100 + Number(bidFloorTypeB))
      );
    });
  };

  // update chart data when the selected metric or aggregation options change
  const updateChartData = (
    selectedMetricKey: IDashboardHomeCardMetricKey,
    shouldAggregateDataByTime: boolean,
    shouldAggregateDataByBidFloorType: boolean,
  ): void => {
    const newChartData = formatDataForChart(
      data,
      selectedMetricKey,
      shouldAggregateDataByTime,
      shouldAggregateDataByBidFloorType,
    );
    unstable_batchedUpdates(() => {
      setIsChartAggregatedByTime(shouldAggregateDataByTime);
      setIsChartAggregatedByBidFloorType(shouldAggregateDataByBidFloorType);
      setSelectedMetricKey(selectedMetricKey);
      setChartData(newChartData);
      setBidFloorKeys(genBidFloorKeys(newChartData, shouldAggregateDataByBidFloorType));
    });
  };

  // fetch live data from the API
  const fetchSmartFloorsLiveData = (isRefreshCall?: boolean): void => {
    if (!isRefreshCall) {
      setLoadState(LoadingStateLoading);
    }

    // return if no site is selected
    if (selectedSmartFloor.ID === 0) {
      unstable_batchedUpdates(() => {
        setData([] as ISmartFloorRealtimeData);
        setChartData([] as IStackedChartData[]);
        setBidFloorKeys([] as string[]);
        setLoadState(LoadingStateNoData);
      });
      return;
    }

    // fetch data for the last 12 hours
    const startTime = moment().minute(0).add(-12, 'hours').utc(); // 12 hours ago
    const endTime = moment().add(1, 'minutes').utc();
    getAdaptiveSmartFloorLiveData(session.publisher.ID, selectedSmartFloor.ID, startTime, endTime)
      .then((smartFloorsLiveData: ISmartFloorRealtimeData) => {
        const isDataEmpty = isEmpty(smartFloorsLiveData);
        const chartData = formatDataForChart(
          smartFloorsLiveData,
          selectedMetricKey,
          isChartAggregatedByTime,
          isChartAggregatedByBidFloorType,
        );
        unstable_batchedUpdates(() => {
          setData(smartFloorsLiveData);
          setChartData(chartData);
          setBidFloorKeys(genBidFloorKeys(chartData, isChartAggregatedByBidFloorType));
          setLoadState(isDataEmpty ? LoadingStateNoData : LoadingStateHasData);
          setIsDataRefreshActive(true);
          setLastRefreshedTime(moment().format('HH:mm:ss'));
          if (isDataEmpty) {
            setSnackbarError(false);
            setSnackbarMessage('No Live Data Found.');
          }
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          if (!isDataRefreshActive) {
            setData([] as ISmartFloorRealtimeData);
            setChartData([] as IStackedChartData[]);
            setBidFloorKeys([] as string[]);
          }
          setLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(
            !isDataRefreshActive
              ? 'Error retrieving data. Please wait and try again.'
              : `Error refreshing data. Data was last successfully updated at ${lastRefreshedTime}.`,
          );
        });
      });
  };

  // refetch live data when the selected site changes
  useEffect(() => {
    fetchSmartFloorsLiveData();
  }, [selectedSite.ID]);

  // refetch live data every 60 seconds
  useInterval(
    () => {
      fetchSmartFloorsLiveData(true);
    },
    isDataRefreshActive ? 60 * 1000 : null,
  );

  return (
    <>
      <SmartFloorsLiveAdaptive
        bidFloorKeys={bidFloorKeys}
        chartData={chartData}
        isChartAggregatedByBidFloorType={isChartAggregatedByBidFloorType}
        isChartAggregatedByTime={isChartAggregatedByTime}
        isLoading={loadState === LoadingStateLoading}
        selectedMetricKey={selectedMetricKey}
        updateChartData={updateChartData}
      />

      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => {
          unstable_batchedUpdates(() => {
            setSnackbarError(false);
            setSnackbarMessage('');
          });
        }}
      >
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
